/**
 * ProductSnapshotActions.tsx
 *
 * Two components:
 *   ExportSnapshotButton  — per product-row download button
 *   ImportSnapshotButton  — page-level import button with conflict modal
 */
import React, { useRef, useState } from 'react';
import {
  Button,
  Collapse,
  Modal,
  Space,
  Table,
  Tree,
  Typography,
  Tag,
  Alert,
  Spin,
  Descriptions,
  List,
  message,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import {
  DownloadOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Product,
  ProvisioningQuestion,
} from '../../types';
import { ProductSnapshot, ImportSummary, ImportPlan, ChangeItem } from '../../types/snapshot';
import { exportProductSnapshot, buildImportPayload, TargetEnvData } from '../../services/snapshotService';
import {
  planImport,
  defaultSelectedIds,
  filterImportResult,
  allPlanIds,
} from '../../services/importSelection';
import { apiService } from '../../services/api';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// ExportSnapshotButton
// ─────────────────────────────────────────────────────────────────────────────

interface ExportSnapshotButtonProps {
  productId: number;
  productName: string;
}

export const ExportSnapshotButton: React.FC<ExportSnapshotButtonProps> = ({
  productId,
  productName,
}) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const snapshot = await exportProductSnapshot(productId);
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = productName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      a.download = `productSnapshot_${safeName}_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(`Snapshot exported for "${productName}"`);
    } catch (err) {
      message.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      size="small"
      icon={<UploadOutlined />}
      loading={exporting}
      onClick={handleExport}
    >
      Export Snapshot
    </Button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot validation
// ─────────────────────────────────────────────────────────────────────────────

interface ValidationIssue {
  level: 'error' | 'warning';
  field: string;
  message: string;
}

function validateSnapshot(
  snapshot: ProductSnapshot,
  targetEnvData: TargetEnvData | null,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // ── Product ─────────────────────────────────────────────────────────────
  if (!snapshot.product.name?.trim()) {
    issues.push({ level: 'error', field: 'Product', message: 'Product name is required.' });
  }

  // ── Entitlements — duplicates within snapshot ─────────────────────────
  const entKeys = new Set<string>();
  snapshot.entitlements.forEach((e, i) => {
    if (!e.name?.trim()) {
      issues.push({ level: 'error', field: `Entitlement #${i + 1}`, message: 'Entitlement name is empty.' });
    }
    if (!e.value?.trim()) {
      issues.push({ level: 'error', field: `Entitlement #${i + 1} (${e.name})`, message: 'Entitlement value is empty.' });
    }
    // name+value alone is not unique — needs_quantity distinguishes otherwise-identical entitlements
    const k = `${e.name}||${e.value}||${e.needs_quantity}`;
    if (entKeys.has(k)) {
      issues.push({ level: 'error', field: 'Entitlements', message: `Duplicate entitlement in snapshot: name="${e.name}" value="${e.value}" needs_quantity=${e.needs_quantity}.` });
    }
    entKeys.add(k);
  });

  // ── Org Entitlements — duplicates within snapshot ────────────────────
  const orgEntKeys = new Set<string>();
  snapshot.orgEntitlements.forEach((e, i) => {
    if (!e.name?.trim()) {
      issues.push({ level: 'error', field: `Org Entitlement #${i + 1}`, message: 'Org entitlement name is empty.' });
    }
    if (!e.access_type?.trim()) {
      issues.push({ level: 'error', field: `Org Entitlement #${i + 1} (${e.name})`, message: 'access_type is required.' });
    }
    const k = `${e.name}||${e.value}||${e.access_type}`;
    if (orgEntKeys.has(k)) {
      issues.push({ level: 'warning', field: 'Org Entitlements', message: `Duplicate org entitlement in snapshot: name="${e.name}" value="${e.value}" access_type="${e.access_type}". Repeated entries will be skipped; the first occurrence will be used.` });
    }
    orgEntKeys.add(k);
  });

  // ── Provisioning Questions — duplicate names within snapshot ──────────
  const pqNames = new Set<string>();
  snapshot.provisioningQuestions.forEach((q, i) => {
    if (!q.name?.trim()) {
      issues.push({ level: 'error', field: `Question #${i + 1}`, message: 'Question name (key) is empty.' });
    }
    if (!q.type?.trim()) {
      issues.push({ level: 'error', field: `Question #${i + 1} (${q.name})`, message: 'Question type is required.' });
    }
    if (pqNames.has(q.name)) {
      issues.push({ level: 'error', field: 'Provisioning Questions', message: `Duplicate question name in snapshot: "${q.name}".` });
    }
    pqNames.add(q.name);
  });

  // ── SKUs — duplicate names within snapshot ────────────────────────────
  const skuNames = new Set<string>();
  snapshot.skus.forEach((sku, i) => {
    if (!sku.name?.trim()) {
      issues.push({ level: 'error', field: `SKU #${i + 1}`, message: 'SKU name is empty.' });
    }
    if (skuNames.has(sku.name)) {
      issues.push({ level: 'error', field: 'SKUs', message: `Duplicate SKU name in snapshot: "${sku.name}".` });
    }
    skuNames.add(sku.name);

    // Entitlements are product-scoped. Validate only the link for this snapshot's
    // product; suite SKU links for other products are resolved in their own imports.
    sku.productLinks.forEach((pl) => {
      const isSnapshotProductLink =
        pl.productRef.name === snapshot.product.name &&
        (pl.productRef.key === undefined || pl.productRef.key === snapshot.product.key);
      if (!isSnapshotProductLink) { return; }

      pl.linkedEntitlementRefs.forEach((ref) => {
        const found = snapshot.entitlements.some(
          (e) =>
            e.name === ref.name &&
            e.value === ref.value &&
            (ref.needs_quantity === undefined || e.needs_quantity === ref.needs_quantity),
        );
        if (!found) {
          issues.push({
            level: 'warning',
            field: `SKU "${sku.name}"`,
            message: `Linked entitlement ref not found in snapshot: name="${ref.name}" value="${ref.value}". Link will be skipped.`,
          });
        }
      });
    });

    // orgEntitlementRefs must exist in snapshot's own orgEntitlements
    sku.orgEntitlementRefs.forEach((ref) => {
      const found = snapshot.orgEntitlements.some(
        (e) => e.name === ref.name && e.value === ref.value && e.access_type === ref.access_type,
      );
      if (!found) {
        issues.push({
          level: 'warning',
          field: `SKU "${sku.name}"`,
          message: `Org entitlement ref not found in snapshot: name="${ref.name}" access_type="${ref.access_type}". Link will be skipped.`,
        });
      }
    });
  });

  // ── Target-env checks (warnings only) ────────────────────────────────
  if (targetEnvData) {
    // Resolve the target product by key first, fall back to name
    const targetProduct = targetEnvData.products.find((p) =>
      snapshot.product.key
        ? p.key === snapshot.product.key
        : p.name === snapshot.product.name,
    );
    const targetProductId = targetProduct?.id;

    // Provisioning questions already in target — scoped to the same product
    snapshot.provisioningQuestions.forEach((q) => {
      const exists = (targetEnvData.provisioningQuestions as ProvisioningQuestion[]).some(
        (tq) =>
          tq.name === q.name &&
          (targetProductId === undefined || tq.product_id === targetProductId),
      );
      if (exists) {
        issues.push({
          level: 'warning',
          field: `Question "${q.name}"`,
          message: `Provisioning question "${q.name}" already exists under product "${snapshot.product.name}" in target — it will be reused.`,
        });
      }
    });
  }

  return issues;
}

// ─────────────────────────────────────────────────────────────────────────────
// ImportSnapshotButton
// ─────────────────────────────────────────────────────────────────────────────

interface ImportSnapshotButtonProps {
  /** Called after a successful import so the parent can refresh its data */
  onImportComplete: () => void;
}

type ModalPhase =
  | 'idle'
  | 'preview'   // review what will be created/updated before confirming
  | 'importing' // API calls in flight
  | 'done';     // import finished, show summary

export const ImportSnapshotButton: React.FC<ImportSnapshotButtonProps> = ({
  onImportComplete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<ModalPhase>('idle');
  const [snapshot, setSnapshot] = useState<ProductSnapshot | null>(null);
  const [conflictProduct, setConflictProduct] = useState<Product | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [targetEnvData, setTargetEnvData] = useState<TargetEnvData | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ── File input handler ──────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) { return; }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string) as ProductSnapshot;
        if (parsed.version !== '2.0' || !parsed.product) {
          message.error(
            'Invalid snapshot file. Please use a file exported from the Data Relationships page (version 2.0).',
          );
          return;
        }
        setSnapshot(parsed);
        await loadTargetAndCheckConflict(parsed);
      } catch {
        message.error('Failed to parse snapshot file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const loadTargetAndCheckConflict = async (snap: ProductSnapshot) => {
    try {
      const [products, entitlements, orgEntitlements, skus, provisioningQuestions, productAllowedRegions, productSkuConfigs, productSkuConfigEntitlements, skuOrgEntitlementConfigs] =
        await Promise.all([
          apiService.getProducts(),
          apiService.getEntitlements(),
          apiService.getOrgEntitlements(),
          apiService.getSKUs(),
          apiService.getAllProvisioningQuestions(),
          apiService.getAllProductAllowedRegions(),
          apiService.getProductSkuConfigs(),
          apiService.getProductSkuConfigEntitlements(),
          apiService.getSKUOrgEntitlementConfigs(),
        ]);

      const envData: TargetEnvData = {
        products,
        entitlements,
        orgEntitlements,
        skus,
        provisioningQuestions,
        productAllowedRegions,
        productSkuConfigs,
        productSkuConfigEntitlements,
        skuOrgEntitlementConfigs,
      };
      setTargetEnvData(envData);

      const conflict = products.find((p) =>
        snap.product.key
          ? p.key === snap.product.key
          : p.name === snap.product.name,
      );

      if (conflict) {
        setConflictProduct(conflict);
      }

      // Build the full diff once, derive the reviewable change plan, and
      // default the selection (creates + updates on, removals off).
      const fullResult = buildImportPayload(snap, envData);
      const importPlan = planImport(fullResult, envData);
      setPlan(importPlan);
      setSelectedIds(defaultSelectedIds(importPlan));

      setPhase('preview');
    } catch {
      message.error('Failed to load target environment data for conflict check.');
    }
  };

  // ── Run the actual import ───────────────────────────────────────────────

  const handleConfirmImport = async () => {
    if (!snapshot || !targetEnvData) { return; }
    setPhase('importing');

    try {
      const fullResult = buildImportPayload(snapshot, targetEnvData);
      const { insertPayload, updatePayload, deletePayload, summary: importSummary } =
        filterImportResult(fullResult, targetEnvData, selectedIds);

      // A DELETE that hits an already-absent row is idempotent — the desired end
      // state (row gone) is already met. Tolerate "record not found"/404 so a
      // stale join row (e.g. sku_org_entitlement_configs) never aborts the whole
      // import and block later, still-needed deletions (e.g. org_entitlements).
      const skippedDeletes: string[] = [];
      const isRecordNotFound = (err: unknown): boolean => {
        const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
        const status = e?.response?.status;
        const body = String(e?.response?.data?.error ?? e?.message ?? '');
        return status === 404 || /record not found/i.test(body);
      };
      const safeDelete = async (fn: () => Promise<void>, label: string) => {
        try {
          await fn();
        } catch (err) {
          if (isRecordNotFound(err)) {
            skippedDeletes.push(label);
            return;
          }
          throw err;
        }
      };

      // ── Deletions first, in FK-safe order (children before parents) ────
      // 1. sku_org_entitlement_configs
      for (const d of deletePayload.skuOrgEntitlementConfigsToDelete) {
        await safeDelete(
          () => apiService.deleteSKUOrgEntitlementConfig(d.skuId, d.orgEntitlementId),
          `sku_org_entitlement_config (sku ${d.skuId} ↔ orgEnt ${d.orgEntitlementId})`,
        );
      }
      // 2. product_sku_config_entitlements
      for (const d of deletePayload.skuConfigEntitlementsToDelete) {
        await safeDelete(
          () => apiService.deleteProductSkuConfigEntitlement(d.productId, d.skuId, d.entitlementId),
          `product_sku_config_entitlement (product ${d.productId}, sku ${d.skuId}, ent ${d.entitlementId})`,
        );
      }
      // 3. product_sku_configs (SKU links to main product)
      for (const d of deletePayload.skuLinksToRemove) {
        await safeDelete(
          () => apiService.deleteProductSkuConfig(d.productId, d.skuId),
          `product_sku_config (product ${d.productId}, sku "${d.skuName}")`,
        );
      }
      // 3b. product_sku_configs (SKU links to OTHER products)
      for (const d of deletePayload.otherProductLinksToRemove) {
        await safeDelete(
          () => apiService.deleteProductSkuConfig(d.productId, d.skuId),
          `product_sku_config (product "${d.productName}", sku "${d.skuName}")`,
        );
      }
      // 4. product_allowed_regions
      for (const d of deletePayload.regionsToDelete) {
        await safeDelete(
          () => apiService.deleteProductAllowedRegion(d.productId, d.regionName),
          `product_allowed_region (product ${d.productId}, "${d.regionName}")`,
        );
      }
      // 5. provisioning_questions
      for (const d of deletePayload.questionsToDelete) {
        await safeDelete(
          () => apiService.deleteProvisioningQuestion(d.id),
          `provisioning_question ("${d.name}")`,
        );
      }
      // 6. org_entitlements
      for (const d of deletePayload.orgEntitlementsToDelete) {
        await safeDelete(
          () => apiService.deleteOrgEntitlement(d.id),
          `org_entitlement ("${d.name}" / "${d.value}")`,
        );
      }
      // 7. entitlements
      for (const d of deletePayload.entitlementsToDelete) {
        await safeDelete(
          () => apiService.deleteEntitlement(d.id),
          `entitlement ("${d.name}" / "${d.value}")`,
        );
      }

      // ── Then upserts ───────────────────────────────────────────────────
      if (insertPayload.bulk_entry.length > 0) {
        await apiService.bulkInsert(insertPayload);
      }
      if (updatePayload) {
        await apiService.bulkUpdate(updatePayload);
      }

      if (skippedDeletes.length > 0) {
        importSummary.unresolvedRefs = [
          ...importSummary.unresolvedRefs,
          ...skippedDeletes.map((s) => `Already removed (skipped): ${s}`),
        ];
      }

      setSummary(importSummary);
      setPhase('done');
      onImportComplete();
    } catch (err) {
      message.error(`Import failed: ${(err as Error).message}`);
      setPhase('preview');
    }
  };

  // ── Reset state ─────────────────────────────────────────────────────────

  const reset = () => {
    setPhase('idle');
    setSnapshot(null);
    setConflictProduct(null);
    setSummary(null);
    setTargetEnvData(null);
    setPlan(null);
    setSelectedIds(new Set());
  };

  // ── Modal content ───────────────────────────────────────────────────────

  const renderModalContent = () => {
    if ((phase === 'preview') && snapshot) {
      const fullPreviewResult = targetEnvData
        ? buildImportPayload(snapshot, targetEnvData)
        : null;
      const previewResult = fullPreviewResult && targetEnvData && plan
        ? filterImportResult(fullPreviewResult, targetEnvData, selectedIds)
        : fullPreviewResult;
      const previewSummary = previewResult?.summary ?? null;
      const deletePayload = previewResult?.deletePayload ?? null;

      // Resolve conflicting product ID once for diff helpers
      const conflictProductId = conflictProduct?.id;
      const insertedProductId = previewResult?.insertPayload.bulk_entry
        .find((group) => group.table_key === 'products')?.entries[0]?.data.ID;
      const snapshotProductId = conflictProductId ??
        (typeof insertedProductId === 'number' ? insertedProductId : undefined);
      const conflictProductRaw = conflictProduct as unknown as Record<string, unknown> | null;

      // ── diff helpers ────────────────────────────────────────────────────
      // isUpdating: true whenever the product already exists in the target env
      const isUpdating = !!conflictProduct;

      /** Returns amber highlight style if the field value differs from the existing target record */
      const diffCell = (snapVal: unknown, existingVal: unknown) => {
        if (!isUpdating) {return {};}
        const a = snapVal ?? null;
        const b = existingVal ?? null;
        return a !== b ? { style: { background: 'var(--snapshot-row-update)', fontWeight: 600 } } : {};
      };

      const findExistingEnt = (name: string, value: string) =>
        targetEnvData?.entitlements.find(
          (e) =>
            e.product_id === conflictProductId &&
            (e.name !== null && e.name !== '' && name !== null && name !== ''
              ? e.name === name && e.value === value
              : e.name === null || e.name === ''
                ? e.value === value
                : e.name === name),
        );

      const findExistingOrgEnt = (name: string, value: string, _access_type: string) =>
        targetEnvData?.orgEntitlements.find(
          (e) =>
            e.product_id === conflictProductId &&
            (e.name !== null && e.name !== '' && name !== null && name !== ''
              ? e.name === name && e.value === value
              : e.name === null || e.name === ''
                ? e.value === value
                : e.name === name),
        );

      const findExistingQuestion = (name: string) =>
        targetEnvData?.provisioningQuestions.find(
          (q) => q.name === name && q.product_id === conflictProductId,
        );

      const entitlementIsChanged = (e: typeof snapshot.entitlements[number]) => {
        const existing = findExistingEnt(e.name, e.value);
        return !existing ||
          (existing.description ?? '') !== (e.description ?? '') ||
          existing.needs_quantity !== e.needs_quantity ||
          (existing.unit ?? '') !== (e.unit ?? '');
      };
      const orgEntitlementIsChanged = (e: typeof snapshot.orgEntitlements[number]) => {
        const existing = findExistingOrgEnt(e.name, e.value, e.access_type);
        return !existing ||
          (existing.description ?? '') !== (e.description ?? '') ||
          existing.needs_quantity !== e.needs_quantity ||
          (existing.unit ?? '') !== (e.unit ?? '') ||
          existing.is_service !== e.is_service;
      };

      const entitlementIdentity = (name: unknown, value: unknown, needsQuantity: unknown) =>
        JSON.stringify([name ?? '', value ?? '', Boolean(needsQuantity)]);
      const orgEntitlementIdentity = (name: unknown, value: unknown, accessType: unknown) =>
        JSON.stringify([name ?? '', value ?? '', accessType ?? '']);
      const visibleEntitlementIdentities = new Set<string>();
      previewResult?.insertPayload.bulk_entry
        .filter((group) => group.table_key === 'entitlements')
        .forEach((group) => group.entries.forEach(({ data }) => {
          visibleEntitlementIdentities.add(entitlementIdentity(data.Name, data.Value, data.NeedsQuantity));
        }));
      previewResult?.updatePayload?.bulk_update
        .filter((group) => group.table_key === 'entitlements')
        .forEach((group) => group.updates.forEach(({ data }) => {
          visibleEntitlementIdentities.add(entitlementIdentity(data.Name, data.Value, data.NeedsQuantity));
        }));
      const visibleOrgEntitlementIdentities = new Set<string>();
      previewResult?.insertPayload.bulk_entry
        .filter((group) => group.table_key === 'org_entitlements')
        .forEach((group) => group.entries.forEach(({ data }) => {
          visibleOrgEntitlementIdentities.add(orgEntitlementIdentity(data.Name, data.Value, data.AccessType));
        }));
      previewResult?.updatePayload?.bulk_update
        .filter((group) => group.table_key === 'org_entitlements')
        .forEach((group) => group.updates.forEach(({ data }) => {
          visibleOrgEntitlementIdentities.add(orgEntitlementIdentity(data.Name, data.Value, data.AccessType));
        }));
      const changedEntitlements = snapshot.entitlements.filter((e) =>
        entitlementIsChanged(e) && visibleEntitlementIdentities.has(
          entitlementIdentity(e.name, e.value, e.needs_quantity),
        ),
      );
      const changedOrgEntitlements = snapshot.orgEntitlements.filter((e) =>
        orgEntitlementIsChanged(e) && visibleOrgEntitlementIdentities.has(
          orgEntitlementIdentity(e.name, e.value, e.access_type),
        ),
      );
      const changedRegions = snapshot.allowedRegions.filter((region) =>
        (previewResult?.insertPayload.bulk_entry ?? []).some((group) =>
          group.table_key === 'product_allowed_regions' && group.entries.some(
            ({ data }) => data.RegionName === region,
          ),
        ) && !targetEnvData?.productAllowedRegions.some(
          (entry) => entry.product_id === conflictProductId && entry.region_name === region,
        ),
      );
      const questionIsChanged = (question: typeof snapshot.provisioningQuestions[number]) => {
        const existing = findExistingQuestion(question.name);
        return !existing ||
          (existing.display_name ?? '') !== (question.display_name ?? '') ||
          (existing.description ?? '') !== (question.description ?? '') ||
          existing.type !== question.type;
      };
      const visibleQuestionNames = new Set<string>();
      previewResult?.insertPayload.bulk_entry
        .filter((group) => group.table_key === 'provisioning_questions')
        .forEach((group) => group.entries.forEach(({ data }) => visibleQuestionNames.add(String(data.Name ?? ''))));
      previewResult?.updatePayload?.bulk_update
        .filter((group) => group.table_key === 'provisioning_questions')
        .forEach((group) => group.updates.forEach(({ data }) => visibleQuestionNames.add(String(data.Name ?? ''))));
      const changedQuestions = snapshot.provisioningQuestions.filter((question) =>
        questionIsChanged(question) && visibleQuestionNames.has(question.name),
      );

      // Keep SKU rows only when the import diff creates a SKU or changes one
      // of its product / entitlement relationships.
      const skuNameById = new Map<number, string>();
      targetEnvData?.skus.forEach((sku) => {
        if (sku.id !== undefined) { skuNameById.set(sku.id, sku.name); }
      });
      const changedSkuNames = new Set<string>();
      const markSkuIdChanged = (id: unknown) => {
        if (typeof id === 'number') {
          const name = skuNameById.get(id);
          if (name) { changedSkuNames.add(name); }
        }
      };
      previewResult?.insertPayload.bulk_entry.forEach((group) => {
        if (group.table_key === 'skus') {
          group.entries.forEach((entry) => {
            const id = entry.data.ID;
            const name = entry.data.Name;
            if (typeof id === 'number' && typeof name === 'string') { skuNameById.set(id, name); }
            if (typeof name === 'string') { changedSkuNames.add(name); }
          });
        } else if (group.table_key === 'product_sku_configs' || group.table_key === 'sku_org_entitlement_configs') {
          group.entries.forEach((entry) => markSkuIdChanged(entry.data.SkuID));
        } else if (group.table_key === 'product_sku_config_entitlements') {
          group.entries.forEach((entry) => markSkuIdChanged(entry.data.ProductSkuConfigSkuID));
        }
      });
      deletePayload?.skuLinksToRemove.forEach((d) => changedSkuNames.add(d.skuName));
      deletePayload?.otherProductLinksToRemove.forEach((d) => changedSkuNames.add(d.skuName));
      deletePayload?.skuConfigEntitlementsToDelete.forEach((d) => markSkuIdChanged(d.skuId));
      deletePayload?.skuOrgEntitlementConfigsToDelete.forEach((d) => markSkuIdChanged(d.skuId));
      const changedSkus = snapshot.skus.filter((sku) => changedSkuNames.has(sku.name));

      const linkKey = (...parts: unknown[]) => JSON.stringify(parts.map((part) => part ?? ''));
      const entitlementById = new Map<number, { name: string; value: string; productId: number }>();
      targetEnvData?.entitlements.forEach((e) => {
        if (e.id !== undefined) { entitlementById.set(e.id, { name: e.name, value: e.value, productId: e.product_id }); }
      });
      const orgEntitlementById = new Map<number, { name: string; value: string; accessType: string }>();
      targetEnvData?.orgEntitlements.forEach((e) => {
        if (e.id !== undefined) { orgEntitlementById.set(e.id, { name: e.name, value: e.value, accessType: e.access_type }); }
      });
      previewResult?.insertPayload.bulk_entry.forEach((group) => {
        if (group.table_key === 'entitlements') {
          group.entries.forEach(({ data }) => {
            if (typeof data.ID === 'number' && typeof data.ProductID === 'number') {
              entitlementById.set(data.ID, {
                name: String(data.Name ?? ''), value: String(data.Value ?? ''), productId: data.ProductID,
              });
            }
          });
        } else if (group.table_key === 'org_entitlements') {
          group.entries.forEach(({ data }) => {
            if (typeof data.ID === 'number') {
              orgEntitlementById.set(data.ID, {
                name: String(data.Name ?? ''), value: String(data.Value ?? ''),
                accessType: String(data.AccessType ?? ''),
              });
            }
          });
        }
      });
      const changedEntitlementLinkKeys = new Set<string>();
      previewResult?.insertPayload.bulk_entry
        .filter((group) => group.table_key === 'product_sku_config_entitlements')
        .forEach((group) => group.entries.forEach(({ data }) => {
          const skuName = skuNameById.get(Number(data.ProductSkuConfigSkuID));
          const entitlement = entitlementById.get(Number(data.EntitlementID));
          if (skuName && entitlement) {
            changedEntitlementLinkKeys.add(linkKey(skuName, entitlement.productId, entitlement.name, entitlement.value));
          }
        }));
      deletePayload?.skuConfigEntitlementsToDelete.forEach((d) => {
        const skuName = skuNameById.get(d.skuId);
        const entitlement = entitlementById.get(d.entitlementId);
        if (skuName && entitlement) {
          changedEntitlementLinkKeys.add(linkKey(skuName, d.productId, entitlement.name, entitlement.value));
        }
      });
      const changedOrgEntitlementLinkKeys = new Set<string>();
      previewResult?.insertPayload.bulk_entry
        .filter((group) => group.table_key === 'sku_org_entitlement_configs')
        .forEach((group) => group.entries.forEach(({ data }) => {
          const skuName = skuNameById.get(Number(data.SkuID));
          const orgEntitlement = orgEntitlementById.get(Number(data.OrgEntitlementID));
          if (skuName && orgEntitlement) {
            changedOrgEntitlementLinkKeys.add(linkKey(
              skuName, orgEntitlement.name, orgEntitlement.value, orgEntitlement.accessType,
            ));
          }
        }));
      deletePayload?.skuOrgEntitlementConfigsToDelete.forEach((d) => {
        const skuName = skuNameById.get(d.skuId);
        const orgEntitlement = orgEntitlementById.get(d.orgEntitlementId);
        if (skuName && orgEntitlement) {
          changedOrgEntitlementLinkKeys.add(linkKey(
            skuName, orgEntitlement.name, orgEntitlement.value, orgEntitlement.accessType,
          ));
        }
      });

      const isSnapshotProductLink = (pl: typeof snapshot.skus[number]['productLinks'][number]) =>
        pl.productRef.name === snapshot.product.name &&
        (pl.productRef.key === undefined || pl.productRef.key === snapshot.product.key);

      const isEntitlementLinkChanged = (
        skuName: string,
        pl: typeof snapshot.skus[number]['productLinks'][number],
        ref: typeof pl.linkedEntitlementRefs[number],
      ) => {
        const linkedProduct = targetEnvData?.products.find(
          (p) => p.name === pl.productRef.name || p.key === pl.productRef.key,
        );
        const productId = linkedProduct?.id ??
          (isSnapshotProductLink(pl) ? snapshotProductId : undefined);
        if (productId === undefined) { return false; }
        return changedEntitlementLinkKeys.has(linkKey(skuName, productId, ref.name, ref.value));
      };

      const isProductLinkChanged = (
        skuName: string,
        pl: typeof snapshot.skus[number]['productLinks'][number],
      ) => {
        if (!isSnapshotProductLink(pl)) { return false; }
        const sku = targetEnvData?.skus.find((s) => s.name === skuName);
        const linkedProduct = targetEnvData?.products.find(
          (p) => p.name === pl.productRef.name || p.key === pl.productRef.key,
        );
        const productId = linkedProduct?.id ?? snapshotProductId;
        if (sku?.id === undefined || productId === undefined) { return true; }
        const hasProductLink = targetEnvData?.productSkuConfigs.some(
          (link) => link.sku_id === sku.id && link.product_id === productId,
        );
        if (!hasProductLink) { return true; }
        const hasChangedEntitlement = pl.linkedEntitlementRefs.some((ref) =>
          isEntitlementLinkChanged(skuName, pl, ref),
        );
        const hasDeletedEntitlement = (deletePayload?.skuConfigEntitlementsToDelete ?? []).some(
          (d) => d.skuId === sku.id && d.productId === productId,
        );
        const productLinkDeleted = (deletePayload?.skuLinksToRemove ?? []).some(
          (d) => d.skuId === sku.id && d.productId === productId,
        );
        return hasChangedEntitlement || hasDeletedEntitlement || productLinkDeleted;
      };

      const isOrgEntitlementLinkChanged = (
        skuName: string,
        ref: typeof snapshot.skus[number]['orgEntitlementRefs'][number],
      ) => {
        return changedOrgEntitlementLinkKeys.has(
          linkKey(skuName, ref.name, ref.value, ref.access_type),
        );
      };

      const validationIssues = validateSnapshot(snapshot, targetEnvData);
      const errors = validationIssues.filter((i) => i.level === 'error');
      const warnings = validationIssues.filter((i) => i.level === 'warning');

      const tblProps = {
        size: 'small' as const,
        bordered: true,
        pagination: false as const,
        style: { marginBottom: 0 },
      };

      const sectionTitle = (
        label: string,
        count: number,
        color: string,
        created?: number,
        updated?: number,
        unchanged?: number,
        deleted?: number,
      ) => (
        <Space size={4}>
          <Text strong style={{ fontSize: 13 }}>{label} <Tag color={color} style={{ marginLeft: 4 }}>{count}</Tag></Text>
          {created !== undefined && created > 0 && <Tag color="green">{created} new</Tag>}
          {updated !== undefined && updated > 0 && <Tag color="blue">{updated} update</Tag>}
          {unchanged !== undefined && unchanged > 0 && <Tag color="default">{unchanged} unchanged</Tag>}
          {deleted !== undefined && deleted > 0 && <Tag color="red">{deleted} delete</Tag>}
        </Space>
      );

      // Build the default-open keys for every non-empty section
      const defaultOpenKeys = [
        'product',
        ...(errors.length > 0 ? ['errors'] : []),
        ...(warnings.length > 0 ? ['warnings'] : []),
        ...(changedEntitlements.length > 0 || (deletePayload?.entitlementsToDelete.length ?? 0) > 0 ? ['entitlements'] : []),
        ...(changedOrgEntitlements.length > 0 || (deletePayload?.orgEntitlementsToDelete.length ?? 0) > 0 ? ['orgEntitlements'] : []),
        ...(changedRegions.length > 0 || (deletePayload?.regionsToDelete.length ?? 0) > 0 ? ['regions'] : []),
        ...(changedQuestions.length > 0 || (deletePayload?.questionsToDelete.length ?? 0) > 0 ? ['questions'] : []),
        ...(changedSkus.length > 0 || (deletePayload?.skuLinksToRemove.length ?? 0) > 0 ? ['skus'] : []),
        ...((previewSummary?.unresolvedRefs.length ?? 0) > 0 ? ['unresolved'] : []),
      ];

      // ── Selective-import change plan ─────────────────────────────────────
      const allIds = plan ? new Set(allPlanIds(plan)) : new Set<string>();
      const itemById = new Map<string, ChangeItem>();
      const lockedIds: string[] = [];
      if (plan) {
        [...plan.creates, ...plan.updates, ...plan.deletes].forEach((i) => {
          itemById.set(i.id, i);
          if (i.locked) { lockedIds.push(i.id); }
        });
      }

      /** Force locked items + dependencies of any selected item to be selected. */
      const enforceSelection = (keys: Set<string>): Set<string> => {
        const next = new Set(keys);
        lockedIds.forEach((id) => next.add(id));
        let changed = true;
        while (changed) {
          changed = false;
          for (const id of Array.from(next)) {
            for (const d of itemById.get(id)?.dependsOn ?? []) {
              if (!next.has(d)) { next.add(d); changed = true; }
            }
          }
        }
        return next;
      };

      const handleCheck = (checkedValue: unknown) => {
        const arr = Array.isArray(checkedValue)
          ? checkedValue
          : (checkedValue as { checked: React.Key[] }).checked;
        const real = new Set(
          (arr as React.Key[]).map(String).filter((k) => allIds.has(k)),
        );
        setSelectedIds(enforceSelection(real));
      };

      const entityLabels: Record<ChangeItem['entity'], string> = {
        product: 'Product',
        sku: 'SKUs',
        entitlement: 'Entitlements',
        orgEntitlement: 'Org Entitlements',
        region: 'Regions',
        question: 'Questions',
        skuLinkChange: 'SKU link changes',
      };
      const entityOrder: ChangeItem['entity'][] = [
        'product', 'sku', 'entitlement', 'orgEntitlement', 'region', 'question', 'skuLinkChange',
      ];

      const itemNode = (i: ChangeItem): DataNode => ({
        key: i.id,
        disableCheckbox: i.locked,
        title: (
          <span>
            {i.label}
            {i.detail ? <Text type="secondary"> — {i.detail}</Text> : null}
            {i.locked ? <Tag color="default" style={{ marginLeft: 6 }}>required</Tag> : null}
          </span>
        ),
      });

      const groupChildren = (groupKey: string, items: ChangeItem[]): DataNode[] => {
        const byEntity = new Map<ChangeItem['entity'], ChangeItem[]>();
        items.forEach((i) => {
          const list = byEntity.get(i.entity) ?? [];
          list.push(i);
          byEntity.set(i.entity, list);
        });
        return entityOrder
          .filter((e) => byEntity.has(e))
          .map((e) => {
            const entityItems = byEntity.get(e) ?? [];
            return {
              key: `${groupKey}:${e}`,
              selectable: false,
              title: <Text>{entityLabels[e]} <Tag>{entityItems.length}</Tag></Text>,
              children: entityItems.map(itemNode),
            };
          });
      };

      const planTreeData: DataNode[] = plan
        ? [
          { key: 'grp:creates', label: 'Additions — will be created', color: 'green', items: plan.creates },
          { key: 'grp:updates', label: 'Updates — existing items changed', color: 'blue', items: plan.updates },
          { key: 'grp:deletes', label: 'Removals — opt-in, off by default', color: 'red', items: plan.deletes },
        ]
          .filter((g) => g.items.length > 0)
          .map((g) => ({
            key: g.key,
            selectable: false,
            title: <Text strong><Tag color={g.color}>{g.items.length}</Tag>{g.label}</Text>,
            children: groupChildren(g.key, g.items),
          }))
        : [];

      const selCount = (items: ChangeItem[]) => items.filter((i) => selectedIds.has(i.id)).length;
      const selectedSummary = plan
        ? `${selCount(plan.creates)}/${plan.creates.length} additions · ` +
          `${selCount(plan.updates)}/${plan.updates.length} updates · ` +
          `${selCount(plan.deletes)}/${plan.deletes.length} removals selected`
        : '';

      return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {/* Row highlight styles — !important to beat Ant Design's specificity */}
          <style>{`
            .snap-row-new > td { background-color: var(--snapshot-row-new) !important; }
            .snap-row-new:hover > td { background-color: var(--snapshot-row-new-hover) !important; }
            .snap-row-update > td { background-color: var(--snapshot-row-update) !important; }
            .snap-row-update:hover > td { background-color: var(--snapshot-row-update-hover) !important; }
            .snap-row-delete > td { background-color: var(--snapshot-row-delete) !important; }
            .snap-row-delete:hover > td { background-color: var(--snapshot-row-delete-hover) !important; }
          `}</style>

          {plan && planTreeData.length > 0 && (
            <Collapse
              defaultActiveKey={['selection']}
              size="small"
              items={[
                {
                  key: 'selection',
                  label: (
                    <Space size={6}>
                      <Text strong style={{ fontSize: 13 }}>Select changes to apply</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{selectedSummary}</Text>
                    </Space>
                  ),
                  children: (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 8 }}
                        message="Only checked items will be applied to the target environment. Additions and updates are pre-selected; removals are opt-in. Selecting a SKU automatically includes any new entitlements it depends on."
                      />
                      <Tree
                        checkable
                        selectable={false}
                        defaultExpandAll
                        checkedKeys={Array.from(selectedIds)}
                        onCheck={handleCheck}
                        treeData={planTreeData}
                      />
                    </>
                  ),
                },
              ]}
            />
          )}

          {errors.length === 0 && warnings.length === 0 && (
            <Alert type="success" showIcon message="All validations passed — ready to import." />
          )}

          <Collapse
            defaultActiveKey={defaultOpenKeys}
            size="small"
            style={{ background: 'transparent' }}
            items={[

              /* ── Validation errors ── */
              ...(errors.length > 0 ? [{
                key: 'errors',
                label: (
                  <Space size={6}>
                    <Tag color="red">{errors.length} error{errors.length > 1 ? 's' : ''}</Tag>
                    <Text type="danger" style={{ fontSize: 13 }}>Import is blocked — fix these first</Text>
                  </Space>
                ),
                children: (
                  <List
                    size="small"
                    dataSource={errors}
                    renderItem={(issue) => (
                      <List.Item style={{ padding: '2px 0', fontSize: 12 }}>
                        <Tag color="red" style={{ marginRight: 6 }}>{issue.field}</Tag>
                        {issue.message}
                      </List.Item>
                    )}
                  />
                ),
              }] : []),

              /* ── Validation warnings ── */
              ...(warnings.length > 0 ? [{
                key: 'warnings',
                label: (
                  <Space size={6}>
                    <Tag color="orange">{warnings.length} warning{warnings.length > 1 ? 's' : ''}</Tag>
                    <Text style={{ fontSize: 13 }}>Import will proceed with adjustments noted below</Text>
                  </Space>
                ),
                children: (
                  <List
                    size="small"
                    dataSource={warnings}
                    renderItem={(issue) => (
                      <List.Item style={{ padding: '2px 0', fontSize: 12 }}>
                        <Tag color="orange" style={{ marginRight: 6 }}>{issue.field}</Tag>
                        {issue.message}
                      </List.Item>
                    )}
                  />
                ),
              }] : []),

              /* ── Product ── */
              {
                key: 'product',
                label: (
                  <Space size={6}>
                    <Text strong style={{ fontSize: 13 }}>Product</Text>
                    <Tag color={previewSummary?.productCreated ? 'green' : previewSummary?.productUpdated ? 'blue' : 'orange'}>
                      {previewSummary?.productCreated ? 'Will create' : previewSummary?.productUpdated ? 'Will update' : 'No changes'}
                    </Tag>
                  </Space>
                ),
                children: (
                  <Table
                    {...tblProps}
                    dataSource={[snapshot.product]}
                    rowKey="name"
                    onRow={() =>
                      isUpdating && conflictProduct && Object.keys(previewSummary?.productFieldDiffs ?? {}).length > 0
                        ? { style: { background: 'var(--snapshot-row-update)' } }
                        : {}
                    }
                    columns={[
                      { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
                      { title: 'Key', dataIndex: 'key', key: 'key', render: (v?: string) => v ? <Tag>{v}</Tag> : '—' },
                      {
                        title: 'Prov. Client', dataIndex: 'provisioning_client', key: 'provisioning_client',
                        onCell: (row) => diffCell(row.provisioning_client ?? null, conflictProductRaw?.['provisioning_client'] ?? null),
                        render: (v?: string) => v ? <Tag color="blue">{v}</Tag> : '—',
                      },
                      {
                        title: 'Hidden', dataIndex: 'is_hidden', key: 'is_hidden', width: 70,
                        onCell: (row) => diffCell(row.is_hidden ?? false, conflictProductRaw?.['is_hidden']),
                        render: (v: boolean) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'Auto Prov.', dataIndex: 'is_auto_provision', key: 'is_auto_provision', width: 90,
                        onCell: (row) => diffCell(row.is_auto_provision ?? false, conflictProductRaw?.['is_auto_provision']),
                        render: (v: boolean) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'Composite', dataIndex: 'is_composite', key: 'is_composite', width: 85,
                        onCell: (row) => diffCell(row.is_composite ?? false, conflictProductRaw?.['is_composite']),
                        render: (v: boolean) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'Brownfield', dataIndex: 'is_brownfield_only', key: 'is_brownfield_only', width: 90,
                        onCell: (row) => diffCell(row.is_brownfield_only ?? false, conflictProductRaw?.['is_brownfield_only']),
                        render: (v: boolean) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'Prov. Style', dataIndex: 'provisioning_style', key: 'provisioning_style',
                        onCell: (row) => diffCell(row.provisioning_style ?? null, conflictProductRaw?.['provisioning_style'] ?? null),
                        render: (v?: string) => v ? <Tag color="blue">{v}</Tag> : '—',
                      },
                    ]}
                  />
                ),
              },

              /* ── Entitlements ── */
              ...(changedEntitlements.length > 0 || (deletePayload?.entitlementsToDelete.length ?? 0) > 0 ? [{
                key: 'entitlements',
                label: sectionTitle('Entitlements', changedEntitlements.length + (deletePayload?.entitlementsToDelete.length ?? 0), 'green', previewSummary?.entitlementsCreated, previewSummary?.entitlementsUpdated, undefined, previewSummary?.entitlementsDeleted),
                children: (
                  <Table
                    {...tblProps}
                    dataSource={[
                      ...changedEntitlements.map((e) => ({ ...e, _key: snapshot.entitlements.indexOf(e), _deleted: false })),
                      ...(deletePayload?.entitlementsToDelete.map((d, i) => ({
                        name: d.name, value: d.value, description: undefined as string | undefined,
                        needs_quantity: false, unit: undefined as string | undefined,
                        _key: 10000 + i, _deleted: true,
                      })) ?? []),
                    ]}
                    rowKey="_key"
                    locale={{ emptyText: 'None' }}
                    rowClassName={(row) => {
                      if ((row as typeof row & { _deleted: boolean })._deleted) {return 'snap-row-delete';}
                      const existing = findExistingEnt(row.name, row.value);
                      if (!isUpdating || !existing) {return 'snap-row-new';}
                      const changed =
                        (existing.description ?? '') !== (row.description ?? '') ||
                        existing.needs_quantity !== row.needs_quantity ||
                        (existing.unit ?? '') !== (row.unit ?? '');
                      return changed ? 'snap-row-update' : '';
                    }}
                    columns={[
                      {
                        title: 'Status', key: '_status', width: 80,
                        render: (_: unknown, row: typeof snapshot.entitlements[0] & { _key: number; _deleted: boolean }) => {
                          if (row._deleted) {return <Tag color="red">Delete</Tag>;}
                          if (!isUpdating) {return <Tag color="green">New</Tag>;}
                          const existing = findExistingEnt(row.name, row.value);
                          if (!existing) {return <Tag color="green">New</Tag>;}
                          const changed =
                            (existing.description ?? '') !== (row.description ?? '') ||
                            existing.needs_quantity !== row.needs_quantity ||
                            (existing.unit ?? '') !== (row.unit ?? '');
                          return changed ? <Tag color="orange">Update</Tag> : <Tag color="default">Same</Tag>;
                        },
                      },
                      { title: 'Name', dataIndex: 'name', key: 'name' },
                      { title: 'Value', dataIndex: 'value', key: 'value' },
                      {
                        title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.description ?? null, findExistingEnt(row.name, row.value)?.description ?? null),
                        render: (v: string | null) => v ?? '—',
                      },
                      {
                        title: 'Needs Qty', dataIndex: 'needs_quantity', key: 'needs_quantity', width: 90,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.needs_quantity, findExistingEnt(row.name, row.value)?.needs_quantity),
                        render: (v: boolean) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.unit ?? null, findExistingEnt(row.name, row.value)?.unit ?? null),
                        render: (v: string | null) => v ?? '—',
                      },
                    ]}
                  />
                ),
              }] : []),

              /* ── Org Entitlements ── */
              ...(changedOrgEntitlements.length > 0 || (deletePayload?.orgEntitlementsToDelete.length ?? 0) > 0 ? [{
                key: 'orgEntitlements',
                label: sectionTitle('Org Entitlements', changedOrgEntitlements.length + (deletePayload?.orgEntitlementsToDelete.length ?? 0), 'purple', previewSummary?.orgEntitlementsCreated, previewSummary?.orgEntitlementsUpdated, undefined, previewSummary?.orgEntitlementsDeleted),
                children: (
                  <Table
                    {...tblProps}
                    dataSource={[
                      ...changedOrgEntitlements.map((e) => ({ ...e, _key: snapshot.orgEntitlements.indexOf(e), _deleted: false })),
                      ...(deletePayload?.orgEntitlementsToDelete.map((d, i) => ({
                        name: d.name, value: d.value, access_type: '' as string,
                        description: undefined as string | undefined, needs_quantity: false,
                        unit: undefined as string | undefined, is_service: false,
                        _key: 10000 + i, _deleted: true,
                      })) ?? []),
                    ]}
                    rowKey="_key"
                    locale={{ emptyText: 'None' }}
                    rowClassName={(row) => {
                      if ((row as typeof row & { _deleted: boolean })._deleted) {return 'snap-row-delete';}
                      const existing = findExistingOrgEnt(row.name, row.value, row.access_type);
                      if (!isUpdating || !existing) {return 'snap-row-new';}
                      const changed =
                        (existing.description ?? '') !== (row.description ?? '') ||
                        existing.needs_quantity !== row.needs_quantity ||
                        (existing.unit ?? '') !== (row.unit ?? '') ||
                        existing.is_service !== row.is_service;
                      return changed ? 'snap-row-update' : '';
                    }}
                    columns={[
                      {
                        title: 'Status', key: '_status', width: 80,
                        render: (_: unknown, row: typeof snapshot.orgEntitlements[0] & { _key: number; _deleted: boolean }) => {
                          if (row._deleted) {return <Tag color="red">Delete</Tag>;}
                          if (!isUpdating) {return <Tag color="green">New</Tag>;}
                          const existing = findExistingOrgEnt(row.name, row.value, row.access_type);
                          if (!existing) {return <Tag color="green">New</Tag>;}
                          const changed =
                            (existing.description ?? '') !== (row.description ?? '') ||
                            existing.needs_quantity !== row.needs_quantity ||
                            (existing.unit ?? '') !== (row.unit ?? '') ||
                            existing.is_service !== row.is_service;
                          return changed ? <Tag color="orange">Update</Tag> : <Tag color="default">Same</Tag>;
                        },
                      },
                      { title: 'Name', dataIndex: 'name', key: 'name' },
                      { title: 'Value', dataIndex: 'value', key: 'value' },
                      { title: 'Access Type', dataIndex: 'access_type', key: 'access_type', width: 110, render: (v: string) => v ? <Tag color="purple">{v}</Tag> : '—' },
                      {
                        title: 'Is Service', dataIndex: 'is_service', key: 'is_service', width: 85,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.is_service, findExistingOrgEnt(row.name, row.value, row.access_type)?.is_service),
                        render: (v: boolean) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'Needs Qty', dataIndex: 'needs_quantity', key: 'needs_quantity', width: 90,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.needs_quantity, findExistingOrgEnt(row.name, row.value, row.access_type)?.needs_quantity),
                        render: (v: boolean) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.unit ?? null, findExistingOrgEnt(row.name, row.value, row.access_type)?.unit ?? null),
                        render: (v: string | null) => v ?? '—',
                      },
                      {
                        title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.description ?? null, findExistingOrgEnt(row.name, row.value, row.access_type)?.description ?? null),
                        render: (v: string | null) => v ?? '—',
                      },
                    ]}
                  />
                ),
              }] : []),

              /* ── Allowed Regions ── */
              ...(changedRegions.length > 0 || (deletePayload?.regionsToDelete.length ?? 0) > 0 ? [{
                key: 'regions',
                label: sectionTitle('Allowed Regions', changedRegions.length + (deletePayload?.regionsToDelete.length ?? 0), 'cyan', previewSummary?.regionsLinked, undefined, undefined, previewSummary?.regionsDeleted),
                children: (
                  <Table
                    {...tblProps}
                    dataSource={[
                      ...changedRegions.map((r) => ({ region: r, _deleted: false })),
                      ...(deletePayload?.regionsToDelete.map((d) => ({ region: d.regionName, _deleted: true })) ?? []),
                    ]}
                    rowKey="region"
                    locale={{ emptyText: 'None' }}
                    onRow={(row) => {
                      if (row._deleted) {return { style: { background: 'var(--snapshot-row-delete)' } };}
                      if (!isUpdating) {return {};}
                      const alreadyPresent = targetEnvData?.productAllowedRegions.some(
                        (r) => r.product_id === conflictProductId && r.region_name === row.region,
                      );
                      return alreadyPresent ? { style: { background: 'var(--snapshot-row-new)' } } : {};
                    }}
                    columns={[
                      {
                        title: 'Status', key: '_status', width: 80,
                        render: (_: unknown, row: { region: string; _deleted: boolean }) => {
                          if (row._deleted) {return <Tag color="red">Delete</Tag>;}
                          if (!isUpdating) {return <Tag color="cyan">New</Tag>;}
                          const alreadyPresent = targetEnvData?.productAllowedRegions.some(
                            (r) => r.product_id === conflictProductId && r.region_name === row.region,
                          );
                          return alreadyPresent ? <Tag color="default">Present</Tag> : <Tag color="cyan">New</Tag>;
                        },
                      },
                      { title: 'Region', dataIndex: 'region', key: 'region', render: (v: string) => <Tag color="cyan">{v}</Tag> },
                    ]}
                  />
                ),
              }] : []),

              /* ── Provisioning Questions ── */
              ...(changedQuestions.length > 0 || (deletePayload?.questionsToDelete.length ?? 0) > 0 ? [{
                key: 'questions',
                label: sectionTitle('Provisioning Questions', changedQuestions.length + (deletePayload?.questionsToDelete.length ?? 0), 'gold', previewSummary?.questionsCreated, previewSummary?.questionsUpdated, undefined, previewSummary?.questionsDeleted),
                children: (
                  <Table
                    {...tblProps}
                    dataSource={[
                      ...changedQuestions.map((q) => ({ ...q, _key: snapshot.provisioningQuestions.indexOf(q), _deleted: false })),
                      ...(deletePayload?.questionsToDelete.map((d, i) => ({
                        name: d.name, display_name: undefined as string | undefined,
                        description: undefined as string | undefined, type: '' as string,
                        _key: 10000 + i, _deleted: true,
                      })) ?? []),
                    ]}
                    rowKey="_key"
                    locale={{ emptyText: 'None' }}
                    rowClassName={(row) => {
                      if ((row as typeof row & { _deleted: boolean })._deleted) {return 'snap-row-delete';}
                      const existing = findExistingQuestion(row.name);
                      if (!isUpdating || !existing) {return 'snap-row-new';}
                      const changed =
                        (existing.display_name ?? '') !== (row.display_name ?? '') ||
                        (existing.description ?? '') !== (row.description ?? '') ||
                        existing.type !== row.type;
                      return changed ? 'snap-row-update' : '';
                    }}
                    columns={[
                      {
                        title: 'Status', key: '_status', width: 80,
                        render: (_: unknown, row: typeof snapshot.provisioningQuestions[0] & { _key: number; _deleted: boolean }) => {
                          if (row._deleted) {return <Tag color="red">Delete</Tag>;}
                          if (!isUpdating) {return <Tag color="green">New</Tag>;}
                          const existing = findExistingQuestion(row.name);
                          if (!existing) {return <Tag color="green">New</Tag>;}
                          const changed =
                            (existing.display_name ?? '') !== (row.display_name ?? '') ||
                            (existing.description ?? '') !== (row.description ?? '') ||
                            existing.type !== row.type;
                          return changed ? <Tag color="orange">Update</Tag> : <Tag color="default">Same</Tag>;
                        },
                      },
                      { title: 'Name (Key)', dataIndex: 'name', key: 'name' },
                      {
                        title: 'Display Name', dataIndex: 'display_name', key: 'display_name',
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.display_name ?? null, findExistingQuestion(row.name)?.display_name ?? null),
                        render: (v: string | null) => v ?? '—',
                      },
                      {
                        title: 'Type', dataIndex: 'type', key: 'type', width: 100,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.type, findExistingQuestion(row.name)?.type),
                        render: (v: string) => v ? <Tag color="geekblue">{v}</Tag> : '—',
                      },
                      {
                        title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true,
                        onCell: (row) => (row as typeof row & { _deleted: boolean })._deleted ? {} : diffCell(row.description ?? null, findExistingQuestion(row.name)?.description ?? null),
                        render: (v: string | null) => v ?? '—',
                      },
                    ]}
                  />
                ),
              }] : []),

              /* ── SKUs ── */
              ...(changedSkus.length > 0 || (deletePayload?.skuLinksToRemove.length ?? 0) > 0 || (deletePayload?.otherProductLinksToRemove.length ?? 0) > 0 ? [{
                key: 'skus',
                label: sectionTitle('SKUs', changedSkus.length + (deletePayload?.skuLinksToRemove.length ?? 0), 'blue', previewSummary?.skusCreated, undefined, undefined, previewSummary?.skuLinksRemoved),
                children: (
                  <Table
                    {...tblProps}
                    dataSource={[
                      ...changedSkus.map((s) => ({ ...s, _key: snapshot.skus.indexOf(s), _deleted: false })),
                      ...(deletePayload?.skuLinksToRemove.map((d, i) => {
                        // Enrich deleted SKU link rows with actual linked entitlements from target env
                        const ceRows = targetEnvData?.productSkuConfigEntitlements.filter(
                          (ce) => ce.product_sku_config_product_id === d.productId && ce.product_sku_config_sku_id === d.skuId,
                        ) ?? [];
                        const linkedEntitlementRefs = ceRows.flatMap((ce) => {
                          const ent = targetEnvData?.entitlements.find((e) => e.id === ce.entitlement_id);
                          return ent ? [{ name: ent.name, value: ent.value }] : [];
                        });
                        const targetProduct = targetEnvData?.products.find((p) => p.id === d.productId);
                        return {
                          name: d.skuName,
                          subscription_buying_model: undefined as string | undefined,
                          is_external: false,
                          productLinks: linkedEntitlementRefs.length > 0 || targetProduct ? [{
                            productRef: { name: targetProduct?.name ?? String(d.productId) },
                            operation: undefined as string | undefined,
                            operand: null as number | null,
                            linkedEntitlementRefs,
                          }] : [] as Array<{ productRef: { name: string }; operation?: string; operand?: number | null; linkedEntitlementRefs: Array<{ name: string; value: string }> }>,
                          orgEntitlementRefs: [] as Array<{ name: string; value: string }>,
                          _key: 10000 + i,
                          _deleted: true,
                        };
                      }) ?? []),
                    ] as Array<typeof snapshot.skus[0] & { _key: number; _deleted: boolean }>}
                    rowKey="_key"
                    locale={{ emptyText: 'None' }}
                    onRow={(row) => {
                      if ((row as typeof row & { _deleted: boolean })._deleted) {return { style: { background: 'var(--snapshot-row-delete)' } };}
                      const existingSku = targetEnvData?.skus.find((s) => s.name === row.name);
                      if (!existingSku) {return {};}
                      return { style: { background: 'var(--snapshot-row-new)' } };
                    }}
                    expandable={{
                      defaultExpandAllRows: true,
                      expandedRowRender: (sku) => {
                        const targetSku = targetEnvData?.skus.find((s) => s.name === sku.name);
                        return (
                          <Space direction="vertical" size={8} style={{ width: '100%', paddingLeft: 8 }}>
                            {sku.productLinks.filter((pl) => isProductLinkChanged(sku.name, pl)).map((pl, pi) => {
                              // Resolve the target product ID for this product link
                              const linkedProduct = targetEnvData?.products.find(
                                (p) => p.name === pl.productRef.name || p.key === pl.productRef.key,
                              );
                              // Compute removed ent refs for this specific (sku, product) pair
                              const deletedRefs: Array<{ name: string; value: string }> =
                                targetSku && linkedProduct
                                  ? (deletePayload?.skuConfigEntitlementsToDelete ?? [])
                                    .filter((d) => d.skuId === targetSku.id && d.productId === linkedProduct.id)
                                    .flatMap((d) => {
                                      const ent = targetEnvData?.entitlements.find((e) => e.id === d.entitlementId);
                                      return ent ? [{ name: ent.name, value: ent.value }] : [];
                                    })
                                  : [];
                              return (
                                <div key={pi} style={{ border: '1px solid var(--snapshot-detail-border)', borderRadius: 6, padding: '8px 12px', background: 'var(--snapshot-detail-bg)' }}>
                                  <Space wrap size={4} style={{ marginBottom: (pl.linkedEntitlementRefs.some((ref) => isEntitlementLinkChanged(sku.name, pl, ref)) || deletedRefs.length > 0) ? 6 : 0 }}>
                                    <Text strong style={{ fontSize: 12 }}>Product:</Text>
                                    <Tag color="default" style={{ fontWeight: 600 }}>
                                      {pl.productRef.name}
                                      {pl.operation ? ` · ${pl.operation}` : ''}
                                      {pl.operand !== null && pl.operand !== undefined ? ` (${pl.operand})` : ''}
                                    </Tag>
                                  </Space>
                                  {(pl.linkedEntitlementRefs.some((ref) => isEntitlementLinkChanged(sku.name, pl, ref)) || deletedRefs.length > 0) && (
                                    <Space wrap size={4}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>Linked Entitlements:</Text>
                                      {pl.linkedEntitlementRefs.filter((ref) => isEntitlementLinkChanged(sku.name, pl, ref)).map((r, ri) => (
                                        <Tag key={`keep-${ri}`} color="green">{r.name}:{r.value}</Tag>
                                      ))}
                                      {deletedRefs.map((r, ri) => (
                                        <Tag key={`del-${ri}`} color="red" style={{ textDecoration: 'line-through' }}>{r.name}:{r.value}</Tag>
                                      ))}
                                    </Space>
                                  )}
                                </div>
                              );
                            })}
                            {/* Removed product links — product_sku_configs that will be deleted */}
                            {(() => {
                              const removedLinks = targetSku
                                ? (deletePayload?.otherProductLinksToRemove ?? []).filter(
                                  (d) => d.skuId === targetSku.id,
                                )
                                : [];
                              return removedLinks.map((d, ri) => (
                                <div key={`removed-pl-${ri}`} style={{ border: '1px solid var(--snapshot-delete-border)', borderRadius: 6, padding: '8px 12px', background: 'var(--snapshot-row-delete)' }}>
                                  <Space wrap size={4}>
                                    <Text strong style={{ fontSize: 12 }}>Product:</Text>
                                    <Tag color="red" style={{ textDecoration: 'line-through', fontWeight: 600 }}>{d.productName}</Tag>
                                    <Tag color="red">Link removed</Tag>
                                  </Space>
                                </div>
                              ));
                            })()}
                            {/* For deleted SKU link rows: show all ent refs being removed across all products */}
                            {(sku as typeof sku & { _deleted?: boolean })._deleted && (() => {
                              const allRemovedRefs = targetSku
                                ? (deletePayload?.skuConfigEntitlementsToDelete ?? [])
                                  .filter((d) => d.skuId === targetSku.id)
                                  .flatMap((d) => {
                                    const ent = targetEnvData?.entitlements.find((e) => e.id === d.entitlementId);
                                    const prod = targetEnvData?.products.find((p) => p.id === d.productId);
                                    return ent ? [{ name: ent.name, value: ent.value, productName: prod?.name ?? String(d.productId) }] : [];
                                  })
                                : [];
                              return allRemovedRefs.length > 0 ? (
                                <div style={{ border: '1px solid var(--snapshot-delete-border)', borderRadius: 6, padding: '8px 12px', background: 'var(--snapshot-row-delete)' }}>
                                  <Space wrap size={4}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Entitlement refs to remove:</Text>
                                    {allRemovedRefs.map((r, ri) => (
                                      <Tag key={ri} color="red" style={{ textDecoration: 'line-through' }}>{r.productName} · {r.name}:{r.value}</Tag>
                                    ))}
                                  </Space>
                                </div>
                              ) : null;
                            })()}
                            {(() => {
                              // Compute removed org entitlement refs for this SKU
                              const removedOrgRefs: Array<{ name: string; value: string }> = targetSku
                                ? (deletePayload?.skuOrgEntitlementConfigsToDelete ?? [])
                                  .filter((d) => d.skuId === targetSku.id)
                                  .flatMap((d) => {
                                    const oe = targetEnvData?.orgEntitlements.find((e) => e.id === d.orgEntitlementId);
                                    return oe ? [{ name: oe.name, value: oe.value }] : [];
                                  })
                                : [];
                              const changedOrgRefs = sku.orgEntitlementRefs.filter((r) =>
                                isOrgEntitlementLinkChanged(sku.name, r),
                              );
                              // Set of org-ent IDs already linked to this SKU in the target
                              const existingOrgEntIds = new Set(
                                targetSku
                                  ? (targetEnvData?.skuOrgEntitlementConfigs ?? [])
                                    .filter((c) => c.sku_id === targetSku.id)
                                    .map((c) => c.org_entitlement_id)
                                  : [],
                              );
                              if (changedOrgRefs.length === 0 && removedOrgRefs.length === 0) { return null; }
                              return (
                                <Space wrap size={4} style={{ marginTop: 4 }}>
                                  <Text type="secondary" style={{ fontSize: 12 }}>Org Entitlements:</Text>
                                  {changedOrgRefs.map((r, ri) => {
                                    const targetOe = targetEnvData?.orgEntitlements.find(
                                      (oe) => oe.name === r.name && oe.value === r.value && oe.access_type === r.access_type,
                                    );
                                    const isNew = !targetOe || targetOe.id === undefined || !existingOrgEntIds.has(targetOe.id);
                                    return isNew
                                      ? (
                                        <Tag key={`oe-new-${ri}`} color="green">
                                          {r.name}:{r.value}
                                          <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700 }}>NEW</span>
                                        </Tag>
                                      )
                                      : <Tag key={`oe-keep-${ri}`} color="purple">{r.name}:{r.value}</Tag>;
                                  })}
                                  {removedOrgRefs.map((r, ri) => (
                                    <Tag key={`oe-del-${ri}`} color="red" style={{ textDecoration: 'line-through' }}>
                                      {r.name}:{r.value}
                                    </Tag>
                                  ))}
                                </Space>
                              );
                            })()}
                          </Space>
                        );
                      },
                    }}
                    columns={[
                      {
                        title: 'Status', key: '_status', width: 90,
                        render: (_: unknown, row: typeof snapshot.skus[0] & { _key: number; _deleted: boolean }) => {
                          if (row._deleted) {return <Tag color="red">Delete Link</Tag>;}
                          const existingSku = targetEnvData?.skus.find((s) => s.name === row.name);
                          const skuLinked = existingSku && targetEnvData?.productSkuConfigs.some(
                            (c) => c.sku_id === existingSku.id && c.product_id === (conflictProductId ?? -1),
                          );
                          if (!existingSku) {return <Tag color="green">New SKU</Tag>;}
                          if (!skuLinked) {return <Tag color="blue">New Link</Tag>;}
                          const hasLinkChanges =
                            row.productLinks.some((pl) => isProductLinkChanged(row.name, pl)) ||
                            row.orgEntitlementRefs.some((ref) => isOrgEntitlementLinkChanged(row.name, ref)) ||
                            (deletePayload?.otherProductLinksToRemove ?? []).some((d) => d.skuId === existingSku.id);
                          if (hasLinkChanges) {return <Tag color="blue">Link changes</Tag>;}
                          return <Tag color="default">Exists</Tag>;
                        },
                      },
                      { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
                      { title: 'Buying Model', dataIndex: 'subscription_buying_model', key: 'subscription_buying_model', width: 120, render: (v?: string) => v ? <Tag color="blue">{v}</Tag> : '—' },
                      { title: 'External', dataIndex: 'is_external', key: 'is_external', width: 75, render: (v: boolean) => v ? 'Yes' : 'No' },
                      {
                        title: 'Linked Products',
                        key: 'productLinks',
                        dataIndex: 'productLinks',
                        render: (_: unknown, row: typeof snapshot.skus[number]) => (
                          <Space wrap size={4}>
                            {row.productLinks.filter((pl) => isProductLinkChanged(row.name, pl)).map((pl, pi) => (
                              <Tag key={pi} color="default">
                                {pl.productRef.name}
                                {pl.operation ? ` · ${pl.operation}` : ''}
                                {pl.operand !== null && pl.operand !== undefined ? ` (${pl.operand})` : ''}
                              </Tag>
                            ))}
                          </Space>
                        ),
                      },
                      {
                        title: 'Org Entitlements',
                        key: 'orgEntitlementRefs',
                        dataIndex: 'orgEntitlementRefs',
                        render: (_: unknown, row: typeof snapshot.skus[number]) => {
                          const refs = row.orgEntitlementRefs.filter((ref) =>
                            isOrgEntitlementLinkChanged(row.name, ref),
                          );
                          return (
                            <Space wrap size={4}>
                              {refs.length > 0
                                ? refs.map((r, ri) => <Tag key={ri} color="purple">{r.name}:{r.value}</Tag>)
                                : <Text type="secondary">—</Text>}
                            </Space>
                          );
                        },
                      },
                    ]}
                    scroll={{ x: 'max-content' }}
                  />
                ),
              }] : []),

              /* ── Unresolved refs ── */
              ...((previewSummary?.unresolvedRefs.length ?? 0) > 0 ? [{
                key: 'unresolved',
                label: (
                  <Space size={6}>
                    <Tag color="orange">{previewSummary?.unresolvedRefs.length} unresolved ref{(previewSummary?.unresolvedRefs.length ?? 0) > 1 ? 's' : ''}</Tag>
                    <Text style={{ fontSize: 13 }}>These links will be skipped during import</Text>
                  </Space>
                ),
                children: (
                  <List
                    size="small"
                    dataSource={previewSummary?.unresolvedRefs ?? []}
                    renderItem={(w) => (
                      <List.Item style={{ padding: '2px 0', fontSize: 12 }}>
                        <WarningOutlined style={{ color: '#faad14', marginRight: 6 }} />{w}
                      </List.Item>
                    )}
                  />
                ),
              }] : []),

            ]}
          />
        </Space>
      );
    }

    if (phase === 'importing') {
      return (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin size="large" tip="Importing…" />
        </div>
      );
    }

    if (phase === 'done' && summary) {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="Import complete"
          />
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Product">
              <Tag color={summary.productCreated ? 'green' : summary.productUpdated ? 'blue' : 'orange'}>
                {summary.productCreated ? 'Created' : summary.productUpdated ? 'Updated' : 'Used existing'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Entitlements">
              {summary.entitlementsCreated > 0 && <Tag color="green">{summary.entitlementsCreated} created</Tag>}
              {summary.entitlementsUpdated > 0 && <Tag color="blue">{summary.entitlementsUpdated} updated</Tag>}
              {summary.entitlementsUnchanged > 0 && <Tag color="default">{summary.entitlementsUnchanged} unchanged</Tag>}
              {summary.entitlementsDeleted > 0 && <Tag color="red">{summary.entitlementsDeleted} deleted</Tag>}
              {summary.entitlementsCreated === 0 && summary.entitlementsUpdated === 0 && summary.entitlementsUnchanged === 0 && summary.entitlementsDeleted === 0 && '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Org Entitlements">
              {summary.orgEntitlementsCreated > 0 && <Tag color="green">{summary.orgEntitlementsCreated} created</Tag>}
              {summary.orgEntitlementsUpdated > 0 && <Tag color="blue">{summary.orgEntitlementsUpdated} updated</Tag>}
              {summary.orgEntitlementsUnchanged > 0 && <Tag color="default">{summary.orgEntitlementsUnchanged} unchanged</Tag>}
              {summary.orgEntitlementsDeleted > 0 && <Tag color="red">{summary.orgEntitlementsDeleted} deleted</Tag>}
              {summary.orgEntitlementsCreated === 0 && summary.orgEntitlementsUpdated === 0 && summary.orgEntitlementsUnchanged === 0 && summary.orgEntitlementsDeleted === 0 && '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Regions linked">
              {summary.regionsLinked > 0 && <Tag color="cyan">{summary.regionsLinked} linked</Tag>}
              {summary.regionsAlreadyPresent > 0 && <Tag color="default">{summary.regionsAlreadyPresent} already present</Tag>}
              {summary.regionsDeleted > 0 && <Tag color="red">{summary.regionsDeleted} deleted</Tag>}
              {summary.regionsLinked === 0 && summary.regionsAlreadyPresent === 0 && summary.regionsDeleted === 0 && '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Questions">
              {summary.questionsCreated > 0 && <Tag color="green">{summary.questionsCreated} created</Tag>}
              {summary.questionsUpdated > 0 && <Tag color="blue">{summary.questionsUpdated} updated</Tag>}
              {summary.questionsUnchanged > 0 && <Tag color="default">{summary.questionsUnchanged} unchanged</Tag>}
              {summary.questionsDeleted > 0 && <Tag color="red">{summary.questionsDeleted} deleted</Tag>}
              {summary.questionsCreated === 0 && summary.questionsUpdated === 0 && summary.questionsUnchanged === 0 && summary.questionsDeleted === 0 && '—'}
            </Descriptions.Item>
            <Descriptions.Item label="SKUs created">
              {summary.skusCreated}
            </Descriptions.Item>
            <Descriptions.Item label="SKU product links">
              {summary.skuLinksCreated > 0 && <Tag color="green">{summary.skuLinksCreated} created</Tag>}
              {summary.skuLinksRemoved > 0 && <Tag color="red">{summary.skuLinksRemoved} removed</Tag>}
              {summary.skuLinksCreated === 0 && summary.skuLinksRemoved === 0 && '—'}
            </Descriptions.Item>
          </Descriptions>

          {summary.unresolvedRefs.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${summary.unresolvedRefs.length} item(s) skipped — see details below`}
              description={
                <List
                  size="small"
                  dataSource={summary.unresolvedRefs}
                  renderItem={(w) => (
                    <List.Item style={{ padding: '2px 0', fontSize: 12 }}>
                      {w}
                    </List.Item>
                  )}
                />
              }
            />
          )}
        </Space>
      );
    }

    return null;
  };

  const modalTitle =
    phase === 'preview'
      ? 'Review Import'
      : phase === 'importing'
        ? 'Importing…'
        : 'Import Complete';

  const modalFooter = (() => {
    if (phase === 'preview') {
      const hasErrors = snapshot
        ? validateSnapshot(snapshot, targetEnvData).some((i) => i.level === 'error')
        : false;
      const nothingSelected = selectedIds.size === 0;
      const disabled = hasErrors || nothingSelected;
      return (
        <Space>
          <Button onClick={reset}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleConfirmImport}
            disabled={disabled}
            title={
              hasErrors
                ? 'Fix all errors before importing'
                : nothingSelected
                  ? 'Select at least one change to apply'
                  : undefined
            }
          >
            Confirm Import
          </Button>
        </Space>
      );
    }
    if (phase === 'done') {
      return (
        <Button type="primary" onClick={reset}>
          Close
        </Button>
      );
    }
    return null;
  })();

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Button
        icon={<DownloadOutlined/>}
        onClick={() => fileInputRef.current?.click()}
      >
        Import Snapshot
      </Button>

      <Modal
        open={phase !== 'idle'}
        title={modalTitle}
        onCancel={phase !== 'importing' ? reset : undefined}
        closable={phase !== 'importing'}
        maskClosable={false}
        footer={modalFooter}
        width={1100}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
        destroyOnClose
      >
        {renderModalContent()}
      </Modal>
    </>
  );
};
